import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  getWorkerFormSchema,
  workerDefaultValues,
  WorkerForm,
  WorkerFormValues,
} from 'forms/WorkerForm'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useCustomSearchCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import {
  UserRole,
  Worker as EMOSWorker,
  WorkerInsertType,
  WorkerSetType,
} from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import {
  ReadWorkersAdminQueryVariables,
  useCreateWorkerMutation,
  useDeleteWorkerMutation,
  useReadWorkersAdminQuery,
  useUpdateWorkerMutation,
} from './AdminWorkersView.generated'
import { AlphabetPagination } from './AlphabetPagination'
import { WorkerList } from './WorkerList'

export const AdminWorkersListView: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [createWorker] = useCreateWorkerMutation()
  const [updateWorker] = useUpdateWorkerMutation()
  const [deleteWorker] = useDeleteWorkerMutation()

  const [formType, setFormType] = React.useState()
  const [routeParams, updateRouteParams] = useRouteParams(
    'admin.data.workers',
    {
      key: 'A',
      search: undefined,
    },
    {
      persist: ['key'],
    }
  )
  const paginationRegex = React.useMemo(
    () => new RegExp(`^${routeParams.key}`, 'i'),
    [routeParams.key]
  )

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.worker.search'),
      onSearch: (value) =>
        updateRouteParams({
          key: 'all',
          search: value,
        }),
      onClear: () =>
        updateRouteParams({
          key: 'A',
          search: undefined,
        }),
    },
    [routeParams, updateRouteParams]
  )

  const { loading, data, error, refetch } = useReadWorkersAdminQuery({
    variables: { search: routeParams.search },
  })

  const items =
    data && data.workers ? data.workers.filter((x) => !x._deleted) : []

  const [selection, selectedItems] = useSelection<EMOSWorker>({
    getKey: (item) => item._id,
  })

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  const handleSubmit = async (
    values: WorkerFormValues,
    formikActions: FormikActions<WorkerFormValues>
  ) => {
    formikActions.setSubmitting(true)
    if (formType === FormType.insert) {
      let pidExists: number
      if (items) {
        pidExists = items.findIndex((worker) => {
          return worker._id === values._id
        })
        if (pidExists !== -1) {
          formikActions.setFieldError(
            '_id',
            'Diese Personalnummer existiert bereits. Personalnummern sind einzigartig und können nur einmal vergeben werden.'
          )
          formikActions.setSubmitting(false)
          selection.setAllSelected(false)
          return
        } else {
          await createWorker({
            variables: { input: values as WorkerInsertType },
          })
        }
      }
    } else {
      const valuesId = values._id
      omitDeep(values, ['_id', '__typename'])
      await updateWorker({
        variables: { id: valuesId, update: values as WorkerSetType },
      })
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)

    refetch({
      variables: { search: routeParams.search },
    } as ReadWorkersAdminQueryVariables)
    hidePanel()
    updateRouteParams({
      key: values.lastName ? values.lastName[0].toUpperCase() : '',
    })
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)

      await deleteWorker({
        variables: { ids },
      })
    }

    hideDialog()
  }

  /** commands */
  useCreateCommand('worker', {
    priority: 10,
    onClick: () => {
      setFormType(FormType.insert)
      showPanel(workerDefaultValues)
    },
  })
  useEditCommand(
    { name: 'worker', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        showPanel(
          omitDeep(selectedItems[0], ['_deleted', 'name', '__typename'])
        )
      },
    },
    [selectedItems]
  )
  // useDeleteCommand(
  //   { name: 'worker', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //     disabled: true,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <AlphabetPagination
        items={items.map((x) => x.lastName)}
        selectedKey={routeParams.key}
        onLinkClick={(item) => updateRouteParams({ key: item!.props.itemKey! })}
        disableAll={!routeParams.search}
      />
      <WorkerList
        items={
          routeParams.key === 'all'
            ? routeParams.search
              ? items
              : []
            : items.filter((item) => paginationRegex.test(item.lastName))
        }
        selection={selection}
        onItemInvoked={() => {
          if (selectedItems.length === 1) {
            setFormType(FormType.update)
            return showPanel(selectedItems[0])
          } else {
            return false
          }
        }}
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={getWorkerFormSchema()}
        headerText={
          formType === FormType.update
            ? t('model.worker.edit')
            : t('model.worker.create')
        }
      >
        <WorkerForm
          isCreateNewForm={formType === FormType.update ? false : true}
        />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminWorkersView: React.FC = () => (
  <ViewAuthorizer
    roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
  >
    <StickyPane>
      <AppCommandBar />
      <AdminWorkersListView />
    </StickyPane>
  </ViewAuthorizer>
)
